import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { GeneralSnackComponent } from '../../components-generals/general-snack/general-snack.component';
import { ModalGeneralComponent } from '../../modals/modal-general/modal-general.component';
import { ModalViewFileComponent } from '../../modals/modal-view-file/modal-view-file.component';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { LoadGeneralComponent } from '../../components-generals/load-general/load-general.component';
import { AdditionalValuesFiles } from '../../models/pages-model/form-general.model';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-input-file-light',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
    LoadGeneralComponent,
    MatTooltipModule,
  ],
  templateUrl: './input-file-light.component.html',
  styleUrl: './input-file-light.component.scss',
})
export class InputFileLightComponent {
  @Input() formControlGeneral: FormControl | AbstractControl | null = new FormControl(
    { value: <File[] | null>null, disabled: false },
    [],
  );
  @Input() maxFiles = 5; // Límite de archivos
  @Input() maxFileSizeMB = 20; // Límite de tamaño de archivo en MB
  @Input() label = 'Subir archivo';
  @Input() accept =
    '.jpg, .jpeg, .bmp, .png, .gif, .tif, .tiff, .xlsx, .zip, .pdf, .xml, .eml, .msg';
  @Input() AdditionalValuesFiles: AdditionalValuesFiles = {
    iD_DOCUMENTOS_COTIZACION: 0,
    iD_CARATULA: 0,
    systemFileName: null,
    fileName: null,
  };
  @Output() emitFile = new EventEmitter<File>(); // Emite el archivo para usarlo en otros componentes
  @Output() emitDeleteFile = new EventEmitter(); // Emite el archivo para usarlo en otros componentes
  @Output() getFileService = new EventEmitter<{
    index: number;
    formControlGeneral: FormControl | AbstractControl;
    AdditionalValuesFiles: AdditionalValuesFiles;
  }>();
  @Output() downloadFileService = new EventEmitter<{
    index: number;
    formControlGeneral: FormControl | AbstractControl;
    AdditionalValuesFiles: AdditionalValuesFiles;
  }>();
  public load = false;
  public files: File[] = [];

  constructor(
    private matSnackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {}

  public handleDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  public handleDrop(event: DragEvent): void {
    event.preventDefault();
    const filesDropped = event.dataTransfer?.files;
    const fileLength = this.formControlGeneral?.value ? this.formControlGeneral.value.length : 0;

    if (!filesDropped || filesDropped.length === 0) {
      return;
    }

    if (fileLength >= this.maxFiles || this.AdditionalValuesFiles.fileName != null) {
      this.viewSnacError(`Máximo ${this.maxFiles} archivos`);
      return;
    }

    const file = filesDropped[0];
    if (file && fileLength < this.maxFiles && this.AdditionalValuesFiles.fileName == null) {
      if (file.size / (1024 * 1024) > this.maxFileSizeMB) {
        this.viewSnacError(`Tamaño máximo de archivo i ${this.maxFileSizeMB} MB`);
      } else {
        const updatedFiles = [
          ...(this.formControlGeneral!.value !== null ? this.formControlGeneral!.value : []),
          file,
        ];
        this.formControlGeneral!.setValue(updatedFiles);
        this.formControlGeneral!.markAsDirty();
        this.formControlGeneral!.markAsTouched();
        this.eventEmitFile(
          this.formControlGeneral!.value[this.formControlGeneral!.value.length - 1],
        );
      }
    }
  }

  public async viewSnacError(mess: string) {
    this.matSnackBar.openFromComponent(GeneralSnackComponent, {
      duration: 5000,
      panelClass: ['panel-snack-general'],
      data: {
        type: 'alert',
        icon: 'close',
        text: 'Error',
        suptext: mess,
        body: true,
      },
    });
  }

  public openModalDelete(index: number): void {
    const fileName =
      this.formControlGeneral!.value && this.formControlGeneral!.value[index]
        ? this.formControlGeneral!.value[index].name
        : this.AdditionalValuesFiles.fileName;

    const dialogRef = this.dialog.open(ModalGeneralComponent, {
      width: 'auto',
      height: 'auto',
      disableClose: true,
      autoFocus: false,
      data: {
        Titule: 'Eliminar archivo',
        buttonClose: true,
        button: 'Eliminar archivo',
        text: '¿Estás seguro que deseas eliminar el archivo ' + fileName + '?',
        data: { delete: true },
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result.delete) {
        if (this.formControlGeneral!.value && this.formControlGeneral!.value[index]) {
          this.deleteFile(index);
        }
        this.formControlGeneral?.setValue([]);
        this.AdditionalValuesFiles = {
          iD_DOCUMENTOS_COTIZACION: 0,
          iD_CARATULA: 0,
          systemFileName: null,
          fileName: null,
        };
        this.emitDeleteFile.emit();
      }
    });
  }

  public deleteFile(index: number): void {
    const files = this.formControlGeneral?.value || []; // Obtiene los archivos actuales
    // Verifica que el índice esté dentro del rango válido

    if (files[index] instanceof File) {
      const updatedFiles = files.filter((_: any, i: number) => i !== index); // Filtra el archivo en el índice especificado
      this.formControlGeneral?.setValue(updatedFiles); // Actualiza el FormControl con el nuevo array
    }
  }

  public eventEmitFile(file: File): void {
    this.emitFile.emit(file);
  }

  public fileInputLight(event: Event): void {
    const fileLength = this.formControlGeneral?.value ? this.formControlGeneral.value.length : 0;
    const input = event.target as HTMLInputElement;
    if (fileLength >= this.maxFiles || this.AdditionalValuesFiles.fileName != null) {
      this.viewSnacError(`Máximo ${this.maxFiles} archivos`);
      input.value = '';
    }
    const file: File | undefined = input.files ? input.files[0] : undefined;
    if (file && fileLength < this.maxFiles && this.AdditionalValuesFiles.fileName == null) {
      if (file.size / (1024 * 1024) > 50) {
        this.viewSnacError(`Tamaño máximo de archivo i ${this.maxFileSizeMB} MB`);
      } else {
        const updatedFiles = [
          ...(this.formControlGeneral?.value !== null ? this.formControlGeneral?.value : []),
          file,
        ];
        this.formControlGeneral?.setValue(updatedFiles);
        this.formControlGeneral?.markAsDirty();
        input.value = '';
        this.eventEmitFile(
          this.formControlGeneral?.value
            ? this.formControlGeneral.value[this.formControlGeneral.value.length - 1]
            : null,
        );
      }
    }
  }

  public viewFileLight(index: number, name?: string): void {
    if (!this.formControlGeneral?.value) {
      return;
    }
    this.dialog.open(ModalViewFileComponent, {
      width: '80vw',
      maxWidth: '80vw',
      maxHeight: '95vh',
      height: '95vh',
      disableClose: false,
      autoFocus: false,
      data: {
        Titule: 'Archivo ' + this.formControlGeneral.value[index].name,
        file: this.formControlGeneral.value[index],
      },
    });
  }

  public activateFormFileErrors(): void {
    if (this.formControlGeneral) {
      this.formControlGeneral.markAsTouched(); // Marca el control como tocado
      this.formControlGeneral.updateValueAndValidity(); // Fuerza la actualización de las validaciones y el estado de errores
    }
  }

  public getFileLigh(index: number) {
    this.getFileService.emit({
      index,
      formControlGeneral: this.formControlGeneral!,
      AdditionalValuesFiles: this.AdditionalValuesFiles,
    });
  }
  public downloadFile(index: number) {
    this.downloadFileService.emit({
      index,
      formControlGeneral: this.formControlGeneral!,
      AdditionalValuesFiles: this.AdditionalValuesFiles,
    });
  }
  isRequired(control: AbstractControl | null): boolean {
    return !!control?.hasValidator?.(Validators.required);
  }
}
